import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useLocale } from 'use-intl';
import { Header, HeaderBackButton, HeaderCopyButton, HeaderUserMenu } from '@/ui/components/header';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Icon } from '@/ui/components/icon';
import { NewOfficeIcon, SparklesIcon, Refresh01Icon } from '@hugeicons-pro/core-stroke-rounded';
import {
  MainContainer,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/ui/components/main';
import { H1, H2 } from '@/ui/components/heading';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import { Field, FieldLabel } from '@/ui/components/form/field';
import { LanguageSelect } from '@/features/i18n/components/language-select';
import { LearningsActivityOverview } from '@/features/learnings/components/learnings-activity-overview';
import { useGenerateClientLearnings } from '@/features/learnings/mutations';
import { learningsQuery } from '@/features/learnings/queries';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@/ui/components/spinner';
import { Button } from '@/ui/components/button';
import { LearningsStatements } from '@/features/learnings/components/learnings-statements';
import { toastManager } from '@/ui/components/toast';
import { dateFormat, formatDate } from '@/dates';
import {
  AISummaryAlert,
  AISummaryAlertCopyButton,
  AISummaryAlertDescription,
  AISummaryAlertTitle,
} from '@/ui/components/ai-summary-alert';

export const Route = createFileRoute('/_authed/_organization/learnings/clients/$clientId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { clientId } = Route.useParams();
  const navigate = useNavigate();
  const locale = useLocale();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const { data, isLoading } = useQuery(learningsQuery(organizationId, undefined, clientId));

  const [rerunOpen, setRerunOpen] = useState(false);
  const [language, setLanguage] = useState<string>('en-US');
  const generateLearnings = useGenerateClientLearnings();

  const learning = data?.insights?.[0];
  if (isLoading) return <Spinner kind="inset" />;
  if (!learning) return null;

  return (
    <>
      <Header>
        <HeaderBackButton to="/learnings" />
        <Breadcrumb>
          <BreadcrumbLink to={'/learnings'}>
            <Icon icon={SparklesIcon} />
            {t({ en: 'Learnings', nb: 'Erfaringer' })}
          </BreadcrumbLink>
          <BreadcrumbPage>
            <Icon icon={NewOfficeIcon} />
            <span className="line-clamp-1">
              {learning.clientName}{' '}
              <span className="lowercase">{t({ en: 'Learnings', nb: 'Erfaringer' })}</span>
            </span>
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton to="/learnings" /> {learning.clientName}{' '}
            <span className="lowercase">{t({ en: 'Learnings', nb: 'Erfaringer' })}</span>
          </H1>
          <AlertDialog open={rerunOpen} onOpenChange={setRerunOpen}>
            <AlertDialogTrigger
              render={
                <Button kind="with-icon" variant="secondary" className="max-md:w-full">
                  <Icon icon={Refresh01Icon} />
                  {t(common.reRunAnalysis)}
                </Button>
              }
            />
            <AlertDialogPopup>
              <AlertDialogTitle>{`${t(common.reRunAnalysis)}?`}</AlertDialogTitle>
              <AlertDialogDescription>{t(common.reRunAnalysisDescription)}</AlertDialogDescription>
              <Field className="mt-2 mb-2">
                <FieldLabel>{t({ en: 'Output language', nb: 'Utdataspråk' })}</FieldLabel>
                <LanguageSelect value={language} onValueChange={setLanguage} />
              </Field>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={generateLearnings.isPending} />
                <Button
                  onClick={() =>
                    generateLearnings.mutate(
                      { clientIds: [clientId], language },
                      {
                        onSuccess: () => {
                          toastManager.add({
                            type: 'success',
                            title: t({
                              en: 'Learning analysis rerun successfully',
                              nb: 'Erfaringsanalyse kjørt på nytt',
                            }),
                          });
                          setRerunOpen(false);
                          navigate({ to: '/learnings' });
                        },
                      },
                    )
                  }
                  pending={generateLearnings.isPending}
                >
                  {t(common.reRunAnalysis)}
                </Button>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        </MainHeader>
        <MainContainer className="pb-0">
          <LearningsActivityOverview clientId={clientId} />
          <section>
            <H2 className="mb-3 mt-5">
              {t({ en: 'Summary', nb: 'Sammendrag' })}{' '}
              {learning.lastUpdatedAt ? (
                <span className="text-muted font-normal ml-1 text-sm">
                  {t({
                    en: `Last updated: ${formatDate(learning.lastUpdatedAt, dateFormat.dayFirst, locale)}`,
                    nb: `Sist oppdatert: ${formatDate(learning.lastUpdatedAt, dateFormat.dayFirst, locale)}`,
                  })}
                </span>
              ) : null}
            </H2>
            <AISummaryAlert text={learning.summary}>
              <AISummaryAlertTitle>
                <AISummaryAlertCopyButton />
              </AISummaryAlertTitle>
              <AISummaryAlertDescription />
            </AISummaryAlert>
          </section>
        </MainContainer>
        <LearningsStatements data={data} />
      </MainScrollArea>
    </>
  );
}
